<template>
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
  >
    <!-- ALERTA ELEGANTE TINTO Y DORADO -->
    <transition name="fade">
      <div v-if="showAlert && !asistenciaConfirmada && !showQRCode" class="custom-alert-overlay" @click.self="cerrarAlerta">
        <div class="custom-alert-card">
          <div class="alert-icon">✨</div>
          <h3 class="alert-title">Confirmación de Asistencia</h3>
          <p class="alert-message">
            Agradecemos de antemano confirmar su asistencia lo antes posible, con el fin de permitir coordinar 
            los detalles del evento con la excelencia que ustedes merecen.
          </p>
          <button class="alert-button" @click="cerrarAlerta">Entendido</button>
        </div>
      </div>
    </transition>

    <div 
      class="confirmation-card"
      ref="formCardRef"
    >
      <!-- CONTENIDO INTERNO DE LA TARJETA -->
      <div class="card-body-content">
        
        <!-- VISTA DE PASE DIGITAL CON CÓDIGO QR / MENSAJE FINAL -->
        <div v-if="asistenciaConfirmada || showQRCode" class="success-view text-center">
          <div v-if="attending === 'no'" class="no-assistance-view">
            <p class="success-message">
              Gracias por avisarnos. Sentimos mucho que no puedas acompañarnos.
            </p>
          </div>
          <div v-else class="qr-view">
            <p class="qr-header">Pase Digital</p>
            <div class="qr-wrapper">
              <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
              <p v-else class="qr-error-message">No se ha generado el código QR</p>
            </div>
            <p class="qr-instructions">Preséntalo el día del evento</p>
          </div>
        </div>

        <!-- FORMULARIO PASO A PASO -->
        <div v-else class="form-wrapper">
          <div class="form-header text-center">
            <h2 ref="tituloAnimado" class="form-title">          <br>

              Confirmar Asistencia
            </h2>
            <p class="rsvp-deadline"></p>

            <div class="progress-container">
              <div
                class="progress-bar"
                :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
              ></div>
            </div>
            <p class="form-subtitle">Paso {{ currentStep }} de {{ totalSteps }}</p>
          </div>

          <form @submit.prevent="handleSubmit" class="confirmation-form">
            <!-- Paso 1 -->
            <div v-if="currentStep === 1" class="step-box">
              <div class="form-group">
                <label class="form-label">Nombre</label>
                <input
                  v-model.trim="name"
                  type="text"
                  class="form-control"
                  placeholder="Escribe tu nombre"
                  required
                >
              </div>
              <div class="form-group">
                <label class="form-label">Teléfono</label>
                <input
                  v-model.trim="phone"
                  type="tel"
                  class="form-control"
                  placeholder="Celular a 10 dígitos"
                  required
                  pattern="[0-9]{10}"
                  title="Ingresa un número de 10 dígitos"
                >
              </div>
            </div>

            <!-- Paso 2 (Solo para eventos no XV / YM) -->
            <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
              <div class="form-group">
                <label class="form-label text-center d-block">¿Invitado por?</label>
                <div class="radio-group">
                  <label><input type="radio" value="novia" v-model="invitedBy" required> Novia</label>
                  <label><input type="radio" value="novio" v-model="invitedBy"> Novio</label>
                </div>
              </div>
            </div>

            <!-- Paso de Asistencia -->
            <div v-if="currentStep === asistenciaStep" class="step-box">
              <label class="form-label text-center d-block">¿Asistirás?</label>
              <div class="radio-group">
                <label><input type="radio" value="si" v-model="attending" required> Sí</label>
                <label><input type="radio" value="no" v-model="attending"> No</label>
              </div>
            </div>

            <!-- Paso de pases / número de asistentes -->
            <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
              <label class="form-label text-center d-block">¿Cuántos asistirán?</label>
              <select v-model.number="guests" class="form-select" :disabled="loading" required>
                <option :value="null" disabled>Selecciona una opción</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <div v-if="loading" class="loading-indicator text-center mt-1">Cargando pases...</div>
            </div>

            <!-- Botones -->
            <div class="d-flex justify-content-between mt-3 buttons-row gap-2">
              <button
                v-if="currentStep > 1"
                type="button"
                class="submit-button secondary"
                @click="prevStep"
                :disabled="isSubmitting"
              >
                Atrás
              </button>

              <button
                v-if="currentStep < totalSteps"
                type="button"
                class="submit-button"
                @click="nextStep"
                :disabled="isSubmitting"
              >
                Siguiente
              </button>

              <button
                v-if="currentStep === totalSteps"
                type="submit"
                class="submit-button"
                :disabled="isSubmitting"
              >
                <span v-if="!isSubmitting">Confirmar</span>
                <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </button>
            </div>

            <div v-if="errorMessage" class="alert alert-danger mt-2 text-center">
              {{ errorMessage }}
            </div>
          </form>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '@/api'
import gsap from 'gsap'
import Swal from 'sweetalert2'

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: {
      type: [Number, String],
      required: true
    },
    nombre: {
      type: String,
      default: ''
    },
    idFestejado: {
      type: Number,
      default: null
    }
  },
  setup(props) {
    const name = ref('')
    const phone = ref('')
    const invitedBy = ref('')
    const attending = ref('')
    const guests = ref(null)
    const guestsOptions = ref([1, 2])
    const loading = ref(true)
    const isSubmitting = ref(false)
    const errorMessage = ref('')

    const qrCode = ref(null)
    const showQRCode = ref(false)
    const asistenciaConfirmada = ref(false)
    const currentStep = ref(1)

    const formCardRef = ref(null)
    const tituloAnimado = ref(null)
    const containerRef = ref(null)

    // Control de la Alerta
    const showAlert = ref(false)
    const alertShown = ref(false)
    let observer = null

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase()
      return ['xv', 'ym'].includes(eventName)
    })

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4)
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3)

    const cerrarAlerta = () => {
      showAlert.value = false
    }

    const animateStep = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box')
        if (!box) return
        gsap.fromTo(
          box,
          { y: 30, opacity: 0, transformPerspective: 300, rotationX: -10 },
          { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', rotationX: 0 }
        )
      })
    }

    const nextStep = async () => {
      errorMessage.value = ''

      // Paso 1: Nombre y teléfono
      if (currentStep.value === 1) {
        if (!name.value.trim() || !phone.value.trim()) {
          errorMessage.value = 'Escribe tu nombre y número de teléfono'
          return
        }
      }

      // Paso 2 (no XV): Invitado por
      if (currentStep.value === 2 && !isXvEvent.value) {
        if (!invitedBy.value) {
          errorMessage.value = 'Selecciona quién te invitó'
          return
        }
      }

      // Paso de Asistencia
      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Selecciona si asistirás o no'
          return
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value
          await nextTick()
          handleSubmit()
          return
        }
      }

      // Paso Final: invitados
      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Selecciona cuántas personas asistirán'
          return
        }
      }

      if (currentStep.value < totalSteps.value) {
        currentStep.value++
        animateStep()
      }
    }

    const prevStep = () => {
      errorMessage.value = ''
      if (currentStep.value > 1) {
        currentStep.value--
        animateStep()
      }
    }

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`
        )
        const numPases = Number(response.data?.num_pase)
        guestsOptions.value = numPases > 0
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2]
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error)
        errorMessage.value = 'Error al cargar las opciones de invitados'
      } finally {
        loading.value = false
      }
    }

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: Number(props.idInvitado)
        })
        if (response.data?.asistio) {
          asistenciaConfirmada.value = true
          await obtenerCodigoQR()
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error)
      }
    }

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, {
          id_invitado: Number(props.idInvitado)
        })
        qrCode.value = response.data?.codigoqr || null
      } catch (error) {
        console.error('Error obteniendo código QR:', error)
        errorMessage.value = 'Error al generar el código QR'
      }
    }

    const handleSubmit = async () => {
      if (attending.value === 'no') guests.value = 0

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: Number(props.idInvitado),
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0))
        }

        await axios.post(`${baseUrl}/asistencia`, formData)

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: '¡Gracias por avisarnos!',
            text: 'Sentimos mucho que no puedas asistir.',
            confirmButtonColor: '#8C1C29',
          })
          asistenciaConfirmada.value = true
        } else {
          await obtenerCodigoQR()
          showQRCode.value = true
          asistenciaConfirmada.value = true
        }
      } catch (error) {
        console.error('Error confirmando asistencia:', error)
        errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.'
      } finally {
        isSubmitting.value = false
      }
    }

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = 0
      } else if (newValue === 'si' && (guests.value === 0 || guests.value == null)) {
        guests.value = 1
      }
    })

    onMounted(async () => {
      await nextTick()

      fetchGuestLimit()
      verificarAsistencia()

      gsap.fromTo(
        formCardRef.value,
        { opacity: 0, y: 30, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power2.out' }
      )

      gsap.fromTo(
        tituloAnimado.value,
        { y: -15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 }
      )

      if (containerRef.value) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !alertShown.value) {
                showAlert.value = true
                alertShown.value = true
              }
            })
          },
          { threshold: 0.3 }
        )
        observer.observe(containerRef.value)
      }
    })

    onUnmounted(() => {
      if (observer) {
        observer.disconnect()
      }
    })

    return {
      name,
      phone,
      invitedBy,
      attending,
      guests,
      guestsOptions,
      loading,
      isSubmitting,
      errorMessage,
      isXvEvent,
      asistenciaConfirmada,
      qrCode,
      showQRCode,
      currentStep,
      totalSteps,
      asistenciaStep,
      nextStep,
      prevStep,
      handleSubmit,
      formCardRef,
      tituloAnimado,
      containerRef,
      showAlert,
      cerrarAlerta
    }
  }
}
</script>

<style scoped>
/* ESTILOS DE LA ALERTA PERSONALIZADA (TINTO / CHIC) */
.custom-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 110vh;
  background: rgba(15, 5, 8, 0.65);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  margin-bottom: 50px;;
}

.custom-alert-card {
  background: #9aa5b8;
  border: 1px solid rgba(212, 175, 55, 0.4);
  border-radius: 16px;
  padding: 1.8rem 1.5rem;
  max-width: 280px;
  width: 80%;
  text-align: center;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  animation: popIn 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.alert-icon {
  font-size: 1.8rem;
  margin-bottom: 0.4rem;
}

.alert-title {
  font-family: 'Playfair Display', serif;
  color: #3c3c3a;
  font-size: 1.35rem;
  margin-bottom: 0.8rem;
  letter-spacing: 0.5px;
}

.alert-message {
  font-family: 'Karla', sans-serif;
  color: #fffffe;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1.4rem;
}

.alert-message strong {
  color: #ffd700;
  font-weight: 600;
}

.alert-button {
background: linear-gradient(
  135deg,
  #f0c9d3 0%,
  #f5f7fa 25%,
  #ffffff 50%,
  #f0c9d3 75%,
  #f0c9d3 100%
);  color: #2b060a;
  font-family: 'Karla', sans-serif;
  font-weight: 700;
  border: none;
  border-radius: 25px;
  padding: 0.55rem 1.8rem;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.alert-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(191, 149, 63, 0.4);
}

@keyframes popIn {
  0% { opacity: 0; transform: scale(0.85) translateY(10px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

/* Transición Vue Fade */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* CONTENEDOR CONTENIDO */
.confirmation-container {
  width: 100%;
  padding: 1rem 0.5rem;
  box-sizing: border-box;
}

/* TARJETA MARCO AZUL */
/* TARJETA MARCO AZUL */
.confirmation-card {
  width: 100%;
  margin-bottom: 50px;
  max-width: 340px !important; /* Ajuste ligero para dar aire a la pantalla */
  min-height: 650px;
  background: url('@/assets/6c0c86813c86b5818dae87a27b7f8bff.png') no-repeat center center;
  background-size: 100% 100%;
  /* AUMENTO RADICAL DEL PADDING LATERAL: de 45px a 75px */
  /* Esto empuja todo el contenido hacia el centro */
  padding: 195px 75px 45px !important; 
  border-radius: 16px;
  box-sizing: border-box;
  position: relative; /* Importante para asegurar el posicionamiento interno */
}

.card-body-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.form-wrapper {
  width: 100%;
}

/* ENCABEZADOS Y TÍTULOS */
.form-header {
  margin-bottom: 0.8rem;
  margin-top:-110px;
}

.form-title {
  font-family: 'ludovica', 'Great Vibes', cursive;
  font-size: 36px !important;
  color: #5999d2;
  text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0 0 2px;
  line-height: 1.1;
  text-align: center;
}

.rsvp-deadline {
  font-family: 'Playfair Display', serif;
  font-size: 10.5px;
  color: rgba(81, 67, 45, 0.95);
  margin: 0 0 8px;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color:#5999d2;
  font-size: 10px;
  margin-top: 3px;
}

/* BARRA DE PROGRESO COMPACTA */
.progress-container {
  height: 4px;
  background-color: rgba(110, 101, 64, 0.3);
  border-radius: 4px;
  overflow: hidden;
  width: 75%;
  margin: 0 auto;
}

.progress-bar {
  height: 100%;
  background-color: #89844c;
  transition: width 0.4s ease;
}

/* CAMPOS DE FORMULARIO - REDUCIDOS EN ANCHO */
.form-group {
  margin-bottom: 0.65rem;
  text-align: left;
}

.form-label {
  display: block;
  margin-bottom: 0.2rem;
  padding-right:30px !important ;
  font-family: 'Karla', sans-serif;
  font-size: 19px !important;
  font-weight: 600;
  color: #2c2626;
}

.form-control,
.form-select {
  width: 100%;
  padding: 0.45rem 0.65rem;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 0.4rem;
  background-color: rgba(255, 255, 255, 0.95);
  color: #333333;
  box-sizing: border-box;
}

.form-control:focus,
.form-select:focus {
  border-color: #5999d2;
  box-shadow: 0 0 0 0.18rem rgba(191, 149, 63, 0.3);
  outline: none;
}

/* OPCIONES SÍ/NO */
.radio-group {
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  padding: 0.2rem 0;
  color: #2a2929;
  font-family: 'Karla', sans-serif;
  font-size: 13px;
}

/* BOTONES CON TEXTURA DORADA */
.submit-button {
  width: 100%;
  padding: 0.5rem 0.65rem;
  font-family: 'Playfair Display', serif;
  font-size: 1.15rem;
  font-weight: 800;
background-image: linear-gradient(
  135deg,
  rgba(255, 255, 255, 0),
  rgba(220, 230, 242, 0.098),
  rgba(180, 195, 215, 0.014)
), url('@/assets/dorado.png');

background-size: cover;  background-size: cover;
  background-position: center;
  background-color: #bf953f;
  color: #1d1c1c !important;
  -webkit-text-fill-color: #000000 !important;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
}

.submit-button.secondary {
  background: rgba(255, 255, 255, 0.85) !important;
  color: #333333 !important;
  -webkit-text-fill-color: #333333 !important;
}

/* MENSAJE DE CONFIRMACIÓN FINAL */
.qr-header {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  color: #1d1c1c;
  margin-bottom: 0.4rem;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: rgba(37, 36, 36, 0.9);
  font-size: 0.85rem;
}

.alert {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 0.4rem;
}

@media (max-width: 576px) {
  .confirmation-card {
    padding: 175px 36px 36px;
    min-height: 580px;
  }

  .form-title {
  }
}
</style>